/**
 * SystemHealthWidget - Live System-Status mit API-Latenz und Modul-Check
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Activity, Database, Globe, Utensils, MessageCircle, CreditCard, 
  RefreshCw, CheckCircle2, AlertTriangle, XCircle, Cpu, Clock,
  Brain, Package, Zap, Settings2
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERVICE_CONFIG = {
  mongodb:         { icon: Database,       label: 'MongoDB' },
  openfoodfacts:   { icon: Utensils,       label: 'OpenFoodFacts' },
  swiss_blv:       { icon: Globe,          label: 'Swiss BLV' },
  whatsapp:        { icon: MessageCircle,  label: 'WhatsApp' },
  llm:             { icon: Brain,          label: 'KI / LLM' },
  payrexx:         { icon: CreditCard,     label: 'Payrexx' },
  modules:         { icon: Package,        label: 'Module' },
  scheduler:       { icon: Settings2,      label: 'Scheduler' },
};

const SystemHealthWidget = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const checkHealth = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/health/detailed`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        timeout: 10000,
      });
      setHealth(r.data);
      setLastChecked(new Date());
    } catch {
      setHealth({ status: 'unhealthy', checks: {}, summary: { ok: 0, total: 0, errors: 1 } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkHealth();
    const iv = setInterval(checkHealth, 60_000);
    return () => clearInterval(iv);
  }, [checkHealth]);

  const statusColor = (s) =>
    s === 'ok' ? 'text-green-600' :
    s === 'degraded' ? 'text-amber-600' :
    s === 'not_configured' ? 'text-gray-400' :
    s === 'error' ? 'text-red-600' : 'text-gray-400';

  const statusBg = (s) =>
    s === 'ok' ? 'bg-green-50' :
    s === 'degraded' ? 'bg-amber-50' :
    s === 'not_configured' ? 'bg-gray-50' :
    s === 'error' ? 'bg-red-50' : 'bg-gray-50';

  const StatusIcon = ({ status }) => {
    if (status === 'ok') return <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />;
    if (status === 'degraded') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    if (status === 'not_configured') return <XCircle className="h-3.5 w-3.5 text-gray-300" />;
    if (status === 'error') return <XCircle className="h-3.5 w-3.5 text-red-500" />;
    return <Activity className="h-3.5 w-3.5 text-gray-400" />;
  };

  const OverallBadge = ({ status }) => {
    if (status === 'healthy') return <Badge className="bg-green-100 text-green-700 text-[10px]">Gesund</Badge>;
    if (status === 'degraded') return <Badge className="bg-amber-100 text-amber-700 text-[10px]">Eingeschraenkt</Badge>;
    return <Badge className="bg-red-100 text-red-700 text-[10px]">Problem</Badge>;
  };

  if (loading && !health) {
    return (
      <Card data-testid="system-health-widget">
        <CardContent className="py-6 flex items-center justify-center gap-2 text-sm text-gray-400">
          <RefreshCw className="h-4 w-4 animate-spin" /> Pruefe System...
        </CardContent>
      </Card>
    );
  }

  const checks = health?.checks || {};
  const summary = health?.summary || {};

  return (
    <Card data-testid="system-health-widget">
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-500" />
            System Status
            {health && <OverallBadge status={health.status} />}
          </CardTitle>
          <div className="flex items-center gap-1">
            {health?.total_check_ms && (
              <span className="text-[10px] text-gray-400 mr-1">{health.total_check_ms}ms</span>
            )}
            <Button variant="ghost" size="sm" onClick={checkHealth} disabled={loading} className="h-7 w-7 p-0">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-3">
        {/* Compact summary row */}
        <div className="flex items-center gap-3 text-xs mb-2">
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle2 className="h-3 w-3" /> {summary.ok || 0}
          </span>
          {(summary.errors || 0) > 0 && (
            <span className="flex items-center gap-1 text-red-600">
              <XCircle className="h-3 w-3" /> {summary.errors}
            </span>
          )}
          {(summary.not_configured || 0) > 0 && (
            <span className="flex items-center gap-1 text-gray-400">
              <XCircle className="h-3 w-3" /> {summary.not_configured} optional
            </span>
          )}
          {health?.uptime_human && (
            <span className="flex items-center gap-1 text-gray-400 ml-auto">
              <Clock className="h-3 w-3" /> {health.uptime_human}
            </span>
          )}
        </div>

        {/* Service grid - always show first 4, expand for rest */}
        <div className="space-y-1">
          {Object.entries(checks).slice(0, expanded ? undefined : 4).map(([key, check]) => {
            const cfg = SERVICE_CONFIG[key] || { icon: Activity, label: key };
            const Icon = cfg.icon;
            return (
              <div key={key} className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${statusBg(check.status)}`}
                   data-testid={`health-service-${key}`}>
                <div className="flex items-center gap-2">
                  <Icon className={`h-3.5 w-3.5 ${statusColor(check.status)}`} />
                  <span className="font-medium text-gray-700">{cfg.label}</span>
                  {check.loaded && check.total && (
                    <span className="text-[10px] text-gray-400">{check.loaded}/{check.total}</span>
                  )}
                  {check.jobs != null && (
                    <span className="text-[10px] text-gray-400">{check.jobs} Jobs</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {check.latency_ms != null && (
                    <span className={`text-[10px] font-mono ${
                      check.latency_ms < 200 ? 'text-green-500' : 
                      check.latency_ms < 500 ? 'text-amber-500' : 'text-red-500'
                    }`}>{check.latency_ms}ms</span>
                  )}
                  <StatusIcon status={check.status} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Expand/Collapse toggle */}
        {Object.keys(checks).length > 4 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full text-center text-[10px] text-blue-500 hover:text-blue-700 mt-1.5 py-0.5"
            data-testid="health-expand-toggle"
          >
            {expanded ? 'Weniger anzeigen' : `+${Object.keys(checks).length - 4} weitere Services`}
          </button>
        )}

        {/* Last checked */}
        {lastChecked && (
          <p className="text-[10px] text-gray-300 text-right mt-1">
            Aktualisiert: {lastChecked.toLocaleTimeString('de-CH')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default SystemHealthWidget;
